import {
    Button,
    Typography,
    Chip,
    Stack,
    Divider,
    Select,
    Option,
    IconButton,
    Input,
    Textarea,
    FormLabel,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    FormControl,
    Sheet,
    Autocomplete,
    createFilterOptions,
    RadioGroup,
    Radio,
    Grid,
} from "@mui/joy";
import { Add, Close, ContentCopy, ArrowBack } from "@mui/icons-material";
import { PageStandard } from "../utils/PageStandard";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PaymentQuote, PaymentQuoteStatus, PaymentRecipient, UserModel } from "../utils/Model";
import {
    cancelPaymentQuote,
    createPaymentQuote,
    getPaymentQuote,
    getPaymentRecipients,
    getUsers,
    sendPaymentQuote,
    updatePaymentQuote,
    updatePaymentQuoteSplits,
} from "../utils/ViewModel";
import { formatCurrency, formatDateTime } from "../utils/Utils";

const PAY_BASE_URL = "https://pay.gocicero.it";

const STATUS_LABELS: Record<PaymentQuoteStatus, string> = {
    [PaymentQuoteStatus.bozza]: "Bozza",
    [PaymentQuoteStatus.inviato]: "Inviato",
    [PaymentQuoteStatus.pagato]: "Pagato",
    [PaymentQuoteStatus.fallito]: "Fallito",
    [PaymentQuoteStatus.scaduto]: "Scaduto",
    [PaymentQuoteStatus.annullato]: "Annullato",
    [PaymentQuoteStatus.rimborsato]: "Rimborsato",
};

const STATUS_COLORS: Record<PaymentQuoteStatus, "neutral" | "primary" | "warning" | "success" | "danger"> = {
    [PaymentQuoteStatus.bozza]: "neutral",
    [PaymentQuoteStatus.inviato]: "primary",
    [PaymentQuoteStatus.pagato]: "success",
    [PaymentQuoteStatus.fallito]: "danger",
    [PaymentQuoteStatus.scaduto]: "warning",
    [PaymentQuoteStatus.annullato]: "neutral",
    [PaymentQuoteStatus.rimborsato]: "warning",
};

const BILLING_TYPE_LABELS: Record<string, string> = {
    persona_fisica: "Persona fisica",
    professionista: "Professionista",
    azienda: "Azienda",
    pa: "Pubblica Amministrazione",
};

interface DraftItem {
    description: string;
    amount: number;
    note: string;
}

function emptyDraftItem(): DraftItem {
    return { description: "", amount: 0, note: "" };
}

interface DraftSplit {
    recipient_id: string;
    amount: number;
    note: string;
}

const userFilter = createFilterOptions<UserModel>();

export function PaymentQuoteEditor() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isCreate = !id;

    const [detail, setDetail] = useState<PaymentQuote | null>(null);
    const [loading, setLoading] = useState(!isCreate);
    const [saving, setSaving] = useState(false);
    const [savingSplits, setSavingSplits] = useState(false);
    const [sending, setSending] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const [users, setUsers] = useState<UserModel[]>([]);
    const [recipients, setRecipients] = useState<PaymentRecipient[]>([]);

    // Cliente
    const [customerMode, setCustomerMode] = useState<"existing" | "manual">("manual");
    const [selectedUser, setSelectedUser] = useState<UserModel | null>(null);
    const [title, setTitle] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [adminNote, setAdminNote] = useState("");

    // Voci
    const [items, setItems] = useState<DraftItem[]>([emptyDraftItem()]);

    // Destinatari
    const [splits, setSplits] = useState<DraftSplit[]>([]);

    useEffect(() => {
        getUsers().then(setUsers).catch(() => setUsers([]));
        getPaymentRecipients().then(setRecipients).catch(() => setRecipients([]));
    }, []);

    useEffect(() => {
        if (isCreate) return;
        setLoading(true);
        getPaymentQuote(id!).then((q) => {
            setDetail(q);
            if (q) {
                setTitle(q.title || "");
                setFirstName(q.customer_first_name || "");
                setLastName(q.customer_last_name || "");
                setEmail(q.customer_email || "");
                setPhone(q.customer_phone || "");
                setAdminNote(q.admin_note || "");
                setItems((q.items || []).length
                    ? q.items!.map((i) => ({ description: i.description, amount: i.amount, note: i.note || "" }))
                    : [emptyDraftItem()]);
                setSplits((q.splits || []).map((s) => ({ recipient_id: s.recipient_id, amount: s.amount, note: s.note || "" })));
            }
            setLoading(false);
        });
    }, [id, isCreate]);

    function handlePickUser(user: UserModel | null) {
        setSelectedUser(user);
        if (user) {
            setFirstName(user.firstname || "");
            setLastName(user.lastname || "");
            setEmail(user.email || "");
        }
    }

    const isBozza = !detail || detail.status === PaymentQuoteStatus.bozza;
    const canEditSplits = !detail || !["pagato", "annullato"].includes(detail.status);

    async function handleSave() {
        setSaving(true);
        const payload = {
            title: title.trim() || null,
            customer_first_name: firstName.trim() || null,
            customer_last_name: lastName.trim() || null,
            customer_email: email.trim(),
            customer_phone: phone.trim() || null,
            admin_note: adminNote.trim() || null,
            items: items.filter((i) => i.description.trim() !== ""),
        };
        if (isCreate) {
            const newId = await createPaymentQuote(payload);
            if (newId) navigate(`/pagamenti/${newId}`, { replace: true });
        } else {
            const ok = await updatePaymentQuote(id!, payload);
            if (ok) setDetail(await getPaymentQuote(id!));
        }
        setSaving(false);
    }

    async function handleSaveSplits() {
        if (!id) return;
        setSavingSplits(true);
        const ok = await updatePaymentQuoteSplits(id, splits.filter((s) => s.recipient_id));
        setSavingSplits(false);
        if (ok) setDetail(await getPaymentQuote(id));
    }

    async function handleSend() {
        if (!id) return;
        setSending(true);
        const ok = await sendPaymentQuote(id);
        setSending(false);
        if (ok) setDetail(await getPaymentQuote(id));
    }

    async function handleCancel() {
        if (!id) return;
        if (!window.confirm("Annullare questo preventivo di pagamento?")) return;
        setCancelling(true);
        const ok = await cancelPaymentQuote(id);
        setCancelling(false);
        if (ok) setDetail(await getPaymentQuote(id));
    }

    function copyLink() {
        if (!id) return;
        navigator.clipboard.writeText(`${PAY_BASE_URL}/${id}`);
        alert("Link copiato negli appunti");
    }

    const totalAmount = items.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
    const assignedAmount = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    const remainingAmount = (detail?.total_amount ?? totalAmount) - assignedAmount;

    return (
        <PageStandard
            title={isCreate ? "Nuovo preventivo di pagamento" : `${detail?.customer_first_name || ""} ${detail?.customer_last_name || ""}`.trim() || "Preventivo di pagamento"}
            sidebarVisible={true}
            headerEnd={
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <IconButton size="sm" variant="soft" onClick={() => navigate("/pagamenti")}>
                        <ArrowBack sx={{ fontSize: 18 }} />
                    </IconButton>
                    {detail && (
                        <Chip size="sm" color={STATUS_COLORS[detail.status]}>{STATUS_LABELS[detail.status]}</Chip>
                    )}
                    {isBozza && (
                        <Button size="sm" loading={saving} onClick={handleSave} disabled={!email.trim()}>
                            Salva
                        </Button>
                    )}
                </Stack>
            }
        >
            {loading ? (
                <Typography level="body-sm" color="neutral">Caricamento…</Typography>
            ) : (
                <Tabs defaultValue="cliente" size="sm">
                    <TabList sx={{ mb: 2 }}>
                        <Tab value="cliente">Cliente</Tab>
                        <Tab value="voci">Voci</Tab>
                        <Tab value="destinatari">Destinatari</Tab>
                        <Tab value="riepilogo">Riepilogo & Invio</Tab>
                        {!isCreate && <Tab value="cronologia">Cronologia</Tab>}
                    </TabList>

                    <TabPanel value="cliente" sx={{ p: 0 }}>
                        <Grid container spacing={3} sx={{ maxWidth: 720 }}>
                            <Grid xs={12}>
                                <FormControl>
                                    <FormLabel>Titolo interno</FormLabel>
                                    <Input size="sm" placeholder="es. Evento Rossi 12/10" value={title} disabled={!isBozza}
                                           onChange={(e) => setTitle(e.target.value)} />
                                </FormControl>
                            </Grid>
                            <Grid xs={12}>
                                <RadioGroup
                                    orientation="horizontal"
                                    value={customerMode}
                                    onChange={(e) => setCustomerMode(e.target.value as "existing" | "manual")}
                                    sx={{ mb: 1 }}
                                >
                                    <Radio value="existing" label="Utente esistente" disabled={!isBozza} />
                                    <Radio value="manual" label="Dati manuali" disabled={!isBozza} />
                                </RadioGroup>
                            </Grid>
                            {customerMode === "existing" && (
                                <Grid xs={12}>
                                    <FormControl>
                                        <FormLabel>Cerca utente</FormLabel>
                                        <Autocomplete
                                            size="sm"
                                            placeholder="Nome, cognome o email"
                                            options={users}
                                            disabled={!isBozza}
                                            value={selectedUser}
                                            onChange={(_, v) => handlePickUser(v)}
                                            getOptionLabel={(o) => `${o.firstname} ${o.lastname} (${o.email})`}
                                            filterOptions={(options, params) => userFilter(options, params)}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.id}>{option.firstname} {option.lastname} – {option.email}</li>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid xs={12} sm={6}>
                                <FormControl>
                                    <FormLabel>Nome cliente</FormLabel>
                                    <Input size="sm" value={firstName} disabled={!isBozza} onChange={(e) => setFirstName(e.target.value)} />
                                </FormControl>
                            </Grid>
                            <Grid xs={12} sm={6}>
                                <FormControl>
                                    <FormLabel>Cognome cliente</FormLabel>
                                    <Input size="sm" value={lastName} disabled={!isBozza} onChange={(e) => setLastName(e.target.value)} />
                                </FormControl>
                            </Grid>
                            <Grid xs={12} sm={6}>
                                <FormControl required>
                                    <FormLabel>Email cliente</FormLabel>
                                    <Input size="sm" type="email" value={email} disabled={!isBozza} onChange={(e) => setEmail(e.target.value)} />
                                </FormControl>
                            </Grid>
                            <Grid xs={12} sm={6}>
                                <FormControl>
                                    <FormLabel>Telefono cliente</FormLabel>
                                    <Input size="sm" value={phone} disabled={!isBozza} onChange={(e) => setPhone(e.target.value)} />
                                </FormControl>
                            </Grid>
                            <Grid xs={12}>
                                <FormControl>
                                    <FormLabel>Nota interna</FormLabel>
                                    <Textarea minRows={2} value={adminNote} disabled={!isBozza} onChange={(e) => setAdminNote(e.target.value)} />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    <TabPanel value="voci" sx={{ p: 0 }}>
                        <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
                            {items.map((item, i) => (
                                <Sheet key={i} variant="outlined" sx={{ p: 1.5, borderRadius: "sm" }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Input
                                                size="sm" placeholder="Descrizione" value={item.description} sx={{ flex: 1 }}
                                                disabled={!isBozza}
                                                onChange={(e) => setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))}
                                            />
                                            {isBozza && (
                                                <IconButton size="sm" variant="plain" color="danger" onClick={() => setItems((arr) => arr.filter((_, idx) => idx !== i))}>
                                                    <Close sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            )}
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                            <FormControl sx={{ flex: 1 }}>
                                                <FormLabel>Importo</FormLabel>
                                                <Input
                                                    size="sm" type="number" value={item.amount} disabled={!isBozza}
                                                    onChange={(e) => setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, amount: Number(e.target.value) || 0 } : x)))}
                                                />
                                            </FormControl>
                                            <FormControl sx={{ flex: 2 }}>
                                                <FormLabel>Nota (opzionale)</FormLabel>
                                                <Input
                                                    size="sm" value={item.note} disabled={!isBozza}
                                                    onChange={(e) => setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, note: e.target.value } : x)))}
                                                />
                                            </FormControl>
                                        </Stack>
                                    </Stack>
                                </Sheet>
                            ))}
                            {isBozza && (
                                <Button size="sm" variant="soft" startDecorator={<Add />}
                                        onClick={() => setItems((arr) => [...arr, emptyDraftItem()])}>
                                    Aggiungi voce
                                </Button>
                            )}
                            <Divider />
                            <Stack direction="row" justifyContent="space-between">
                                <Typography level="title-sm">Totale</Typography>
                                <Typography level="title-sm">{formatCurrency(detail?.total_amount ?? totalAmount)}</Typography>
                            </Stack>
                        </Stack>
                    </TabPanel>

                    <TabPanel value="destinatari" sx={{ p: 0 }}>
                        {isCreate ? (
                            <Typography level="body-sm" color="neutral">
                                Salva il preventivo prima di assegnare i destinatari.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
                                <Typography level="body-xs" color="neutral">
                                    Suddividi il totale del preventivo tra i destinatari Stripe Connect. Non è
                                    obbligatorio assegnarlo tutto: la parte non assegnata resta a GoCicero.
                                </Typography>
                                {splits.map((split, i) => (
                                    <Sheet key={i} variant="outlined" sx={{ p: 1.5, borderRadius: "sm" }}>
                                        <Stack direction="row" spacing={1} alignItems="flex-end">
                                            <FormControl sx={{ flex: 2 }}>
                                                <FormLabel>Destinatario</FormLabel>
                                                <Select
                                                    size="sm" disabled={!canEditSplits}
                                                    value={split.recipient_id || null}
                                                    onChange={(_, v) => setSplits((arr) => arr.map((x, idx) => (idx === i ? { ...x, recipient_id: v as string } : x)))}
                                                >
                                                    {recipients.filter((r) => r.active || r.id === split.recipient_id).map((r) => (
                                                        <Option key={r.id} value={r.id}>{r.name}</Option>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl sx={{ flex: 1 }}>
                                                <FormLabel>Importo</FormLabel>
                                                <Input
                                                    size="sm" type="number" value={split.amount} disabled={!canEditSplits}
                                                    onChange={(e) => setSplits((arr) => arr.map((x, idx) => (idx === i ? { ...x, amount: Number(e.target.value) || 0 } : x)))}
                                                />
                                            </FormControl>
                                            <FormControl sx={{ flex: 2 }}>
                                                <FormLabel>Nota (opzionale)</FormLabel>
                                                <Input
                                                    size="sm" value={split.note} disabled={!canEditSplits}
                                                    onChange={(e) => setSplits((arr) => arr.map((x, idx) => (idx === i ? { ...x, note: e.target.value } : x)))}
                                                />
                                            </FormControl>
                                            {canEditSplits && (
                                                <IconButton size="sm" variant="plain" color="danger" onClick={() => setSplits((arr) => arr.filter((_, idx) => idx !== i))}>
                                                    <Close sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Sheet>
                                ))}
                                {canEditSplits && (
                                    <Button size="sm" variant="soft" startDecorator={<Add />}
                                            onClick={() => setSplits((arr) => [...arr, { recipient_id: "", amount: 0, note: "" }])}>
                                        Aggiungi destinatario
                                    </Button>
                                )}
                                <Divider />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography level="body-sm">Totale preventivo</Typography>
                                    <Typography level="body-sm">{formatCurrency(detail?.total_amount ?? totalAmount)}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography level="body-sm">Assegnato ai destinatari</Typography>
                                    <Typography level="body-sm">{formatCurrency(assignedAmount)}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography level="body-sm" color={remainingAmount < 0 ? "danger" : "neutral"}>
                                        {remainingAmount < 0 ? "Supera il totale di" : "Non assegnato (resta a GoCicero)"}
                                    </Typography>
                                    <Typography level="body-sm" color={remainingAmount < 0 ? "danger" : "neutral"}>
                                        {formatCurrency(Math.abs(remainingAmount))}
                                    </Typography>
                                </Stack>
                                {canEditSplits && (
                                    <Button size="sm" loading={savingSplits} onClick={handleSaveSplits} disabled={remainingAmount < 0}>
                                        Salva destinatari
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </TabPanel>

                    <TabPanel value="riepilogo" sx={{ p: 0 }}>
                        <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
                            {detail ? (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography level="title-sm">Totale addebitato al cliente</Typography>
                                    <Typography level="title-sm">{formatCurrency(detail.total_amount)}</Typography>
                                </Stack>
                            ) : (
                                <Typography level="body-sm" color="neutral">
                                    Salva il preventivo per vedere il riepilogo calcolato dal server.
                                </Typography>
                            )}

                            {detail && detail.status !== PaymentQuoteStatus.bozza && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Input size="sm" readOnly value={`${PAY_BASE_URL}/${detail.id}`} sx={{ flex: 1 }} />
                                    <IconButton size="sm" variant="soft" onClick={copyLink}>
                                        <ContentCopy sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Stack>
                            )}

                            {detail?.billingInfo ? (
                                <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: "sm" }}>
                                    <Typography level="title-sm" sx={{ mb: 1 }}>Dati di fatturazione</Typography>
                                    <Stack spacing={0.5}>
                                        <Typography level="body-sm">
                                            {detail.billingInfo.company_name || `${detail.billingInfo.first_name || ""} ${detail.billingInfo.last_name || ""}`.trim()}
                                            {" · "}{BILLING_TYPE_LABELS[detail.billingInfo.customer_type]}{" · "}{detail.billingInfo.country}
                                        </Typography>
                                        {(detail.billingInfo.tax_code || detail.billingInfo.vat_number) && (
                                            <Typography level="body-xs" color="neutral">
                                                {detail.billingInfo.tax_code && `CF: ${detail.billingInfo.tax_code}`}
                                                {detail.billingInfo.tax_code && detail.billingInfo.vat_number && " · "}
                                                {detail.billingInfo.vat_number && `P.IVA: ${detail.billingInfo.vat_number}`}
                                            </Typography>
                                        )}
                                        <Typography level="body-xs" color="neutral">
                                            {detail.billingInfo.address} {detail.billingInfo.street_number}, {detail.billingInfo.postal_code} {detail.billingInfo.city} {detail.billingInfo.province && `(${detail.billingInfo.province})`}
                                        </Typography>
                                        {(detail.billingInfo.pec || detail.billingInfo.sdi_code) && (
                                            <Typography level="body-xs" color="neutral">
                                                {detail.billingInfo.pec && `PEC: ${detail.billingInfo.pec}`}
                                                {detail.billingInfo.pec && detail.billingInfo.sdi_code && " · "}
                                                {detail.billingInfo.sdi_code && `SDI: ${detail.billingInfo.sdi_code}`}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Sheet>
                            ) : detail && (
                                <Typography level="body-sm" color="neutral">
                                    Il cliente non ha ancora compilato i dati di fatturazione.
                                </Typography>
                            )}

                            <Stack direction="row" spacing={1}>
                                {isBozza && !isCreate && (
                                    <Button size="sm" color="primary" loading={sending} onClick={handleSend}>
                                        Invia al cliente
                                    </Button>
                                )}
                                {[PaymentQuoteStatus.fallito, PaymentQuoteStatus.scaduto].includes(detail?.status as PaymentQuoteStatus) && (
                                    <Button size="sm" color="primary" loading={sending} onClick={handleSend}>
                                        Reinvia al cliente
                                    </Button>
                                )}
                                {detail && ![PaymentQuoteStatus.pagato, PaymentQuoteStatus.annullato].includes(detail.status) && (
                                    <Button size="sm" color="danger" variant="soft" loading={cancelling} onClick={handleCancel}>
                                        Annulla preventivo
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </TabPanel>

                    {!isCreate && (
                        <TabPanel value="cronologia" sx={{ p: 0 }}>
                            <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
                                {!detail?.statusHistory?.length && (
                                    <Typography level="body-sm" color="neutral">Nessuno storico disponibile.</Typography>
                                )}
                                {detail?.statusHistory?.map((h) => (
                                    <Sheet key={h.id} variant="outlined" sx={{ p: 1.5, borderRadius: "sm" }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Chip size="sm" color={STATUS_COLORS[h.status]}>{STATUS_LABELS[h.status]}</Chip>
                                            <Typography level="body-xs" color="neutral">{formatDateTime(h.changed_at)}</Typography>
                                        </Stack>
                                        {h.note && <Typography level="body-sm" sx={{ mt: 0.5 }}>{h.note}</Typography>}
                                    </Sheet>
                                ))}
                            </Stack>
                        </TabPanel>
                    )}
                </Tabs>
            )}
        </PageStandard>
    );
}
